/* This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 */

export interface CoordinateMapping {
  mouseMultiX: number;
  mouseMultiY: number;
  mouseOffsetX: number;
  mouseOffsetY: number;
  centerOffsetX: number;
  centerOffsetY: number;
  scrollX: number;
  scrollY: number;
  frameW: number;
  frameH: number;
}

export interface VideoCoordinates {
  x: number;
  y: number;
}

export class CoordinateMapper {
  private readonly videoElement: HTMLVideoElement;
  public mapping: CoordinateMapping | null = null;

  constructor(videoElement: HTMLVideoElement) {
    this.videoElement = videoElement;
  }

  updateMapping(): void {
    const rect = this.videoElement.getBoundingClientRect();
    // Use getBoundingClientRect for actual rendered size; fall back to offsetWidth/Height
    // if the element is not yet visible (rect.width/height would be 0).
    const displayWidth = rect.width || this.videoElement.offsetWidth;
    const displayHeight = rect.height || this.videoElement.offsetHeight;
    const videoWidth = this.videoElement.videoWidth;
    const videoHeight = this.videoElement.videoHeight;

    const scale = Math.min(
      displayWidth / videoWidth,
      displayHeight / videoHeight,
    );

    const scaledWidth = videoWidth * scale;
    const scaledHeight = videoHeight * scale;

    this.mapping = {
      mouseMultiX: videoWidth / scaledWidth,
      mouseMultiY: videoHeight / scaledHeight,
      mouseOffsetX: Math.max((displayWidth - scaledWidth) / 2.0, 0),
      mouseOffsetY: Math.max((displayHeight - scaledHeight) / 2.0, 0),
      // Store the element's page-relative position so clientToVideo can
      // correctly subtract it from viewport-relative clientX/Y coordinates.
      // rect.left/top are viewport-relative; adding scrollX/Y makes them page-relative,
      // which matches the existing formula: (clientX - centerOffsetX + scrollX).
      centerOffsetX: rect.left + window.scrollX,
      centerOffsetY: rect.top + window.scrollY,
      scrollX: window.scrollX,
      scrollY: window.scrollY,
      frameW: videoWidth,
      frameH: videoHeight,
    };
  }

  updateScrollOffset(): void {
    if (this.mapping) {
      this.mapping.scrollX = window.scrollX;
      this.mapping.scrollY = window.scrollY;
    }
  }

  clientToVideo(clientX: number, clientY: number): VideoCoordinates {
    if (!this.mapping) this.updateMapping();

    const x = this.clampCoordinate(
      Math.round(
        (clientX -
          this.mapping!.mouseOffsetX -
          this.mapping!.centerOffsetX +
          this.mapping!.scrollX) *
          this.mapping!.mouseMultiX,
      ),
      this.mapping!.frameW,
    );

    const y = this.clampCoordinate(
      Math.round(
        (clientY -
          this.mapping!.mouseOffsetY -
          this.mapping!.centerOffsetY +
          this.mapping!.scrollY) *
          this.mapping!.mouseMultiY,
      ),
      this.mapping!.frameH,
    );

    return { x, y };
  }

  private clampCoordinate(value: number, maxValue: number): number {
    if (value === maxValue - 1) return maxValue;
    if (value > maxValue) return maxValue;
    if (value < 0) return 0;
    return value;
  }

  isReady(): boolean {
    return this.mapping !== null;
  }
}
